/**
 * Printable 8.5×11 "Jump It or Fix It" station signs (one page per station, shared design).
 * Based on the Fresh Meet puzzle-station template (QR + optional question preview).
 */
import { openPrintWindow } from './printUtils';

const SPLATTER_SVG = `<svg class="fm-splatter" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
<circle cx="24" cy="24" r="7" fill="#F47B20"/>
<ellipse cx="11" cy="14" rx="4" ry="2.5" fill="#F47B20" transform="rotate(-30 11 14)"/>
<ellipse cx="37" cy="12" rx="3" ry="1.8" fill="#F47B20" transform="rotate(20 37 12)"/>
<ellipse cx="40" cy="32" rx="3.5" ry="2" fill="#F47B20" transform="rotate(-10 40 32)"/>
<ellipse cx="14" cy="36" rx="2.5" ry="1.5" fill="#F47B20" transform="rotate(15 14 36)"/>
<circle cx="30" cy="38" r="2" fill="#F47B20"/>
<circle cx="8" cy="28" r="1.5" fill="#F47B20"/>
</svg>`;

function escapeHtml(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function absoluteImageUrl(url, origin) {
  if (!url || !String(url).trim()) return '';
  const t = String(url).trim();
  if (/^https?:\/\//i.test(t)) return t;
  if (t.startsWith('//')) return `https:${t}`;
  if (t.startsWith('/')) return `${origin}${t}`;
  return t;
}

/** Full document CSS (appended after base print CSS). */
export function getJumpItStationSignPrintCss() {
  return `
  @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:ital,wght@0,400;0,700;0,900;1,900&family=Barlow:wght@400;500;700;900&display=swap');
  :root {
    --orange:   #F47B20;
    --deep-burn:#D96510;
    --blackout: #1A1A1A;
    --charcoal: #3D3D3D;
    --gray:     #C8C8C8;
    --cloud:    #F5F4F0;
    --white:    #FFFFFF;
  }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body {
    font-family: 'Barlow Condensed', 'Arial Narrow', Impact, Arial, sans-serif;
    background: var(--cloud);
    padding: 0;
  }
  .jumpit-sign-sheet { display: block; }
  .jumpit-sign-sheet .card {
    width: 8.5in;
    min-height: 11in;
    background: var(--cloud);
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    page-break-after: always;
    page-break-inside: avoid;
  }
  .jumpit-sign-sheet .card:last-child { page-break-after: auto; }
  .speed-bg {
    position: absolute;
    inset: 0;
    z-index: 0;
    overflow: hidden;
  }
  .speed-bg::before {
    content: '';
    position: absolute;
    top: -40%;
    left: -10%;
    width: 130%;
    height: 200%;
    background: repeating-linear-gradient(
      -62deg,
      transparent 0px,
      transparent 38px,
      rgba(244,123,32,0.06) 38px,
      rgba(244,123,32,0.06) 42px
    );
  }
  .speed-bg::after {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: repeating-linear-gradient(
      -62deg,
      transparent 0px,
      transparent 78px,
      rgba(61,61,61,0.04) 78px,
      rgba(61,61,61,0.04) 80px
    );
  }
  .header {
    position: relative;
    z-index: 10;
    background: var(--blackout);
    padding: 18px 36px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
  }
  .fm-logo { display: flex; align-items: center; gap: 8px; }
  .fm-logo-img { height: 52px; width: auto; display: block; }
  .fm-splatter { width: 48px; height: 48px; margin-left: 4px; flex-shrink: 0; }
  .header-badge {
    background: var(--orange);
    color: var(--white);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    padding: 5px 12px;
    border-radius: 3px;
    white-space: nowrap;
  }
  .orange-rule { height: 5px; background: var(--orange); position: relative; z-index: 10; }
  .puzzle-label-strip {
    position: relative;
    z-index: 10;
    background: var(--charcoal);
    padding: 7px 36px;
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
  }
  .puzzle-label-strip .dot {
    width: 8px; height: 8px;
    background: var(--orange);
    border-radius: 50%;
    flex-shrink: 0;
  }
  .puzzle-label-strip span {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--gray);
  }
  .puzzle-label-strip .pipe { color: var(--orange); opacity: 0.5; }
  .main {
    position: relative;
    z-index: 5;
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 0 50px 40px;
  }
  .title-block { margin-top: 44px; position: relative; text-align: center; }
  .eyebrow {
    display: inline-block;
    background: var(--orange);
    color: var(--white);
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    padding: 6px 18px;
    border-radius: 3px;
    margin-bottom: 18px;
  }
  .puzzle-title {
    font-size: 108px;
    font-weight: 900;
    font-style: italic;
    text-transform: uppercase;
    letter-spacing: -0.025em;
    line-height: 0.88;
    color: var(--blackout);
    position: relative;
    display: block;
  }
  .puzzle-title::after {
    content: attr(data-text);
    position: absolute;
    top: 6px;
    left: 6px;
    z-index: -1;
    color: var(--orange);
    opacity: 0.18;
  }
  .title-or {
    display: inline-block;
    color: var(--orange);
    font-size: 58px;
    font-weight: 900;
    font-style: italic;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    line-height: 1.1;
    margin: 4px 0;
  }
  .title-block::before {
    content: '';
    position: absolute;
    top: -12px; left: 0;
    width: 60px; height: 5px;
    background: var(--orange);
    border-radius: 2px;
  }
  .title-block::after {
    content: '';
    position: absolute;
    bottom: -12px; right: 0;
    width: 60px; height: 5px;
    background: var(--orange);
    border-radius: 2px;
  }
  .divider { margin: 40px 0 32px; display: flex; flex-direction: column; gap: 3px; }
  .divider-charcoal { height: 2px; background: var(--charcoal); opacity: 0.3; }
  .divider-orange { height: 1px; background: var(--orange); opacity: 0.7; }
  .two-col { display: flex; gap: 40px; align-items: stretch; flex: 1; }
  .sponsor-col { flex: 1; display: flex; flex-direction: column; gap: 14px; }
  .col-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--gray);
  }
  .sponsor-box {
    flex: 1;
    border: 3px dashed var(--gray);
    border-radius: 6px;
    background: rgba(255,255,255,0.65);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    min-height: 220px;
    padding: 28px;
    position: relative;
  }
  .sponsor-box::before {
    content: '';
    position: absolute;
    top: -3px; left: -3px;
    width: 28px; height: 5px;
    background: var(--orange);
    border-radius: 2px 0 0 0;
  }
  .sponsor-box::after {
    content: '';
    position: absolute;
    bottom: -3px; right: -3px;
    width: 28px; height: 5px;
    background: var(--orange);
    border-radius: 0 0 2px 0;
  }
  .jumpit-sign-sponsor-img {
    max-width: 180px;
    max-height: 130px;
    object-fit: contain;
  }
  .sponsor-icon {
    width: 64px; height: 64px;
    background: var(--gray);
    border-radius: 50%;
    opacity: 0.35;
  }
  .sponsor-placeholder-text {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--charcoal);
    text-align: center;
    line-height: 1.35;
  }
  .sponsor-sub {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--gray);
    opacity: 0.6;
    text-align: center;
  }
  .qr-col { flex: 1.15; display: flex; flex-direction: column; gap: 14px; }
  .qr-box {
    flex: 1;
    background: var(--blackout);
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 20px;
    padding: 32px 28px;
    position: relative;
    overflow: hidden;
    min-height: 220px;
  }
  .qr-box::before {
    content: '';
    position: absolute;
    inset: 0;
    background: repeating-linear-gradient(
      -55deg,
      transparent 0px,
      transparent 16px,
      rgba(244,123,32,0.07) 16px,
      rgba(244,123,32,0.07) 18px
    );
  }
  .qr-inner {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 18px;
    width: 100%;
  }
  .qr-frame {
    background: var(--white);
    border-radius: 6px;
    padding: 8px;
    flex-shrink: 0;
  }
  .qr-img { width: 150px; height: 150px; display: block; }
  .qr-instruction-main {
    font-size: 22px;
    font-weight: 900;
    font-style: italic;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--white);
    text-align: center;
    line-height: 1.15;
  }
  .qr-instruction-main em { color: var(--orange); font-style: italic; }
  .qr-instruction-sub {
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--gray);
    text-align: center;
    opacity: 0.7;
  }
  .footer {
    position: relative;
    z-index: 10;
    margin-top: 36px;
    padding: 14px 50px;
    background: var(--blackout);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
  }
  .footer-left {
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--gray);
    opacity: 0.6;
  }
  .footer-right {
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--orange);
    opacity: 0.8;
  }
  .bg-number {
    position: absolute;
    bottom: 80px;
    right: -20px;
    font-size: 400px;
    font-weight: 900;
    font-style: italic;
    color: var(--orange);
    opacity: 0.03;
    line-height: 1;
    z-index: 1;
    user-select: none;
    pointer-events: none;
  }
  @media print {
    body { padding: 0; background: white; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .jumpit-sign-sheet .card { box-shadow: none; }
    @page { size: letter portrait; margin: 0; }
  }
  `;
}

function buildStationCard({
  station,
  stationIndex,
  origin,
  publicCode,
  stripVenue,
  stripTagline,
  footerLeft,
  footerRight,
}) {
  const stationId = (station.stationId || '').trim();
  const name = (station.name || stationId || `Station ${stationIndex + 1}`).trim();
  const scanUrl = stationId && publicCode ? `${origin}/p/${publicCode}/${encodeURIComponent(stationId)}` : '';
  const qrSrc = scanUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(scanUrl)}`
    : '';
  const imgSrc = absoluteImageUrl(station.questionImage, origin);
  const letter = (station.letter || '').trim();
  const bgGlyph =
    letter && letter.toLowerCase() !== 'start'
      ? letter.slice(0, 3)
      : String(stationIndex + 1);

  const sponsorInner = imgSrc
    ? `<img src="${escapeHtml(imgSrc)}" alt="" class="jumpit-sign-sponsor-img" />
       <div class="sponsor-placeholder-text">${escapeHtml(name)}</div>`
    : `<div class="sponsor-icon"></div>
       <div class="sponsor-placeholder-text">${escapeHtml(name)}</div>
       <div class="sponsor-sub">Add a question photo in admin to preview gear here</div>`;

  const qrInner = qrSrc
    ? `<div class="qr-frame"><img class="qr-img" src="${escapeHtml(qrSrc)}" width="150" height="150" alt="Scan to play" /></div>`
    : `<div class="qr-frame"><div class="sponsor-sub" style="color:#fff;padding:40px 20px;text-align:center;">Add station ID + public code to generate QR</div></div>`;

  return `
<div class="card">
  <div class="speed-bg"></div>
  <header class="header">
    <div class="fm-logo">
      <img class="fm-logo-img" src="${escapeHtml(origin)}/fresh-meet-logo.png" alt="Fresh Meet" />
      ${SPLATTER_SVG}
    </div>
    <div class="header-badge">Puzzle Station</div>
  </header>
  <div class="orange-rule"></div>
  <div class="puzzle-label-strip">
    <div class="dot"></div>
    <span>Fresh Meet</span>
    <span class="pipe">|</span>
    <span>${escapeHtml(stripVenue)}</span>
    <span class="pipe">|</span>
    <span>${escapeHtml(stripTagline)}</span>
  </div>
  <div class="main">
    <div class="bg-number">${escapeHtml(bgGlyph)}</div>
    <div class="title-block">
      <div class="eyebrow">⚡ ${escapeHtml(name)} ⚡</div>
      <div class="puzzle-title" data-text="JUMP IT"><span class="title-line-1">JUMP IT</span></div>
      <div class="title-or">— or —</div>
      <div class="puzzle-title" data-text="FIX IT"><span class="title-line-2">FIX IT</span></div>
    </div>
    <div class="divider">
      <div class="divider-charcoal"></div>
      <div class="divider-orange"></div>
    </div>
    <div class="two-col">
      <div class="sponsor-col">
        <div class="col-label">Station preview</div>
        <div class="sponsor-box">
          ${sponsorInner}
        </div>
      </div>
      <div class="qr-col">
        <div class="col-label" style="color: var(--gray);">How To Play</div>
        <div class="qr-box">
          <div class="qr-inner">
            ${qrInner}
            <div class="qr-instruction-main">SCAN THIS &amp;<br>ENTER YOUR <em>PASSCODE</em></div>
            <div class="qr-instruction-sub">Point your camera · tap the link · type it in</div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <footer class="footer">
    <div class="footer-left">${escapeHtml(footerLeft)}</div>
    <div class="footer-right">${escapeHtml(footerRight)}</div>
  </footer>
</div>`;
}

/**
 * @param {object} opts
 * @param {Array<object>} opts.stations
 * @param {string} opts.publicCode
 * @param {string} opts.origin - e.g. window.location.origin
 * @param {string} [opts.stripVenue]
 * @param {string} [opts.stripTagline]
 * @param {string} [opts.footerLeft]
 * @param {string} [opts.footerRight]
 */
export function buildJumpItStationSignsBodyHtml(opts) {
  const {
    stations,
    publicCode,
    origin,
    stripVenue = 'Bonus puzzle',
    stripTagline = 'Scan · Solve · Score',
    footerLeft = 'Fresh Meet',
    footerRight = 'freshmeet.com',
  } = opts;

  const list = Array.isArray(stations) ? stations : [];
  const cards = list
    .map((station, i) =>
      buildStationCard({
        station,
        stationIndex: i,
        origin,
        publicCode,
        stripVenue,
        stripTagline,
        footerLeft,
        footerRight,
      })
    )
    .join('\n');

  return `<div class="jumpit-sign-sheet">${cards}</div>`;
}

/**
 * Opens the browser print dialog with one letter page per station.
 */
export function printJumpItStationSigns(opts) {
  const {
    documentTitle = 'Jump It or Fix It — station signs',
    returnUrl,
    ...rest
  } = opts;
  const origin = rest.origin || (typeof window !== 'undefined' ? window.location.origin : '');
  const body = buildJumpItStationSignsBodyHtml({ ...rest, origin });
  const css = getJumpItStationSignPrintCss();
  const back =
    returnUrl != null
      ? returnUrl
      : typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search}`
        : '';
  openPrintWindow(documentTitle, body, css, back);
}
